// src/services/companionService.js
/**
 * Companion Service - Manages Beastbound Ranger companion lifecycle
 * Companions are stored as a subcollection under the ranger's character document
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import { hasSubclass } from '../utils/featureDetection';

/**
 * Available companion experiences - ranger chooses 2 at creation
 */
export const COMPANION_EXPERIENCES = [
  { id: 'bold-distraction', name: 'Bold Distraction', bonus: 2 },
  { id: 'expert-climber', name: 'Expert Climber', bonus: 2 },
  { id: 'fetch', name: 'Fetch', bonus: 2 },
  { id: 'fierce', name: 'Fierce', bonus: 2 },
  { id: 'loyal', name: 'Loyal', bonus: 2 },
  { id: 'stealthy', name: 'Stealthy', bonus: 2 },
  { id: 'tracker', name: 'Tracker', bonus: 2 },
  { id: 'watchful', name: 'Watchful', bonus: 2 }
];

/**
 * Companion level-up upgrade options
 */
export const COMPANION_UPGRADES = [
  {
    id: 'intelligent',
    name: 'Intelligent',
    description: 'Companion gains a permanent +1 bonus to a Companion Experience of your choice'
  },
  {
    id: 'light-in-the-dark',
    name: 'Light in the Dark',
    description: 'Ranger gains an extra Hope slot when companion is present',
    affectsRanger: true
  },
  {
    id: 'creature-comfort',
    name: 'Creature Comfort',
    description: 'Once per long rest, spend action with companion to clear Hope or Stress',
    affectsRanger: true
  },
  {
    id: 'armored',
    name: 'Armored',
    description: 'When companion would mark Stress, ranger can mark Armor slot instead',
    affectsRanger: true
  },
  {
    id: 'vicious',
    name: 'Vicious',
    description: 'Upgrade damage die (d6→d8→d10→d12) or damage range (Melee→Close/Close→Far)'
  },
  {
    id: 'resilient',
    name: 'Resilient',
    description: 'Companion gains +1 max Stress'
  },
  {
    id: 'bonded',
    name: 'Bonded',
    description: 'When ranger is down, companion can attempt rescue (roll d6s = unmarked Stress, 6 = revive)'
  },
  {
    id: 'aware',
    name: 'Aware',
    description: 'Companion gains +2 Evasion'
  }
];

/**
 * Create a new companion for a ranger
 * @param {string} rangerId - Character ID of the ranger
 * @param {Object} companionData - Companion creation data
 * @returns {Promise<string>} Companion document ID
 */
export const createCompanion = async (rangerId, companionData) => {
  try {
    // Validate ranger exists and is Beastbound
    const rangerRef = doc(db, 'characters', rangerId);
    const rangerSnap = await getDoc(rangerRef);

    if (!rangerSnap.exists()) {
      throw new Error('Ranger character not found');
    }

    const ranger = rangerSnap.data();
    if (!hasSubclass(ranger, 'beastbound')) {
      throw new Error('Only Beastbound rangers can have companions');
    }

    // Check if companion already exists
    const existingCompanion = await getCompanion(rangerId);
    if (existingCompanion) {
      throw new Error('This ranger already has a companion');
    }

    // Validate companion data
    if (!companionData.name || !companionData.animalType) {
      throw new Error('Companion name and animal type are required');
    }

    if (!companionData.experiences || companionData.experiences.length !== 2) {
      throw new Error('Companion must have exactly 2 experiences');
    }

    if (!companionData.attackDescription) {
      throw new Error('Companion attack description is required');
    }

    // Process upgrade selections if provided
    const upgradeSelections = companionData.upgradeSelections || [];
    let processedStats = {
      evasion: 10 + (ranger.level - 1), // +1 per level
      maxStress: 3,
      damageDie: 'd6',
      damageRange: 'Melee',
      experiences: companionData.experiences.map(exp => ({
        id: exp.id,
        name: exp.name,
        bonus: exp.bonus || 2
      })),
      levelUpChoices: []
    };

    // Apply upgrade selections
    for (let i = 0; i < upgradeSelections.length; i++) {
      const upgrade = upgradeSelections[i];
      const levelForUpgrade = i + 2; // Level 2 for first upgrade, etc.

      // Record the choice
      processedStats.levelUpChoices.push({
        level: levelForUpgrade,
        upgrade: upgrade.id
      });

      // Apply the upgrade effects
      switch (upgrade.id) {
        case 'intelligent':
          // +1 to first experience (would need UI selection in future for choosing)
          if (processedStats.experiences.length > 0) {
            processedStats.experiences[0].bonus += 1;
          }
          break;
        case 'vicious':
          // Upgrade damage die (default to damage, would need UI selection)
          const dieProgression = ['d6', 'd8', 'd10', 'd12'];
          const currentDieIndex = dieProgression.indexOf(processedStats.damageDie);
          if (currentDieIndex < dieProgression.length - 1) {
            processedStats.damageDie = dieProgression[currentDieIndex + 1];
          }
          break;
        case 'resilient':
          // +1 max Stress
          processedStats.maxStress += 1;
          break;
        case 'aware':
          // +2 Evasion (in addition to level-up evasion)
          processedStats.evasion += 2;
          break;
        // Light in the Dark, Creature Comfort, Armored, Bonded are passive - just record
        default:
          break;
      }
    }

    // Create companion document
    const companionRef = doc(collection(db, 'characters', rangerId, 'companion'));
    const companion = {
      name: companionData.name,
      animalType: companionData.animalType,
      imageUrl: companionData.imageUrl || '',

      // Combat stats (with upgrades applied)
      evasion: processedStats.evasion,
      stress: 0, // Current stress (unmarked)
      maxStress: processedStats.maxStress,

      // Experiences (2 at creation, potentially modified by upgrades)
      experiences: processedStats.experiences,

      // Attack
      attackDescription: companionData.attackDescription,
      damageDie: processedStats.damageDie,
      damageRange: processedStats.damageRange,

      // Level tracking
      level: ranger.level || 1,
      levelUpChoices: processedStats.levelUpChoices,
      pendingUpgrades: 0, // No pending upgrades if created with selections

      // Metadata
      rangerId: rangerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(companionRef, companion);

    console.log('🐾 Companion created at level', ranger.level, 'with', upgradeSelections.length, 'upgrades applied');

    toast.success(`${companion.name} has joined your party!`);
    return companionRef.id;

  } catch (error) {
    console.error('Error creating companion:', error);
    toast.error(error.message || 'Failed to create companion');
    throw error;
  }
};

/**
 * Get the companion for a ranger (if exists)
 * @param {string} rangerId - Character ID of the ranger
 * @returns {Promise<Object|null>} Companion data or null
 */
export const getCompanion = async (rangerId) => {
  try {
    const companionCollectionRef = collection(db, 'characters', rangerId, 'companion');
    const q = query(companionCollectionRef, limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const companionDoc = querySnapshot.docs[0];
    return {
      id: companionDoc.id,
      ...companionDoc.data()
    };

  } catch (error) {
    console.error('Error getting companion:', error);
    return null;
  }
};

/**
 * Update companion data
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateCompanion = async (rangerId, companionId, updates) => {
  try {
    const companionRef = doc(db, 'characters', rangerId, 'companion', companionId);

    await updateDoc(companionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Error updating companion:', error);
    toast.error('Failed to update companion');
    throw error;
  }
};

/**
 * Apply a level-up upgrade to the companion
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @param {Object} upgradeChoice - The upgrade being applied
 * @param {Object} upgradeData - Additional data for the upgrade (e.g., which experience to improve)
 * @returns {Promise<void>}
 */
export const levelUpCompanion = async (rangerId, companionId, upgradeChoice, upgradeData = {}) => {
  try {
    const companionRef = doc(db, 'characters', rangerId, 'companion', companionId);
    const companionSnap = await getDoc(companionRef);

    if (!companionSnap.exists()) {
      throw new Error('Companion not found');
    }

    const companion = companionSnap.data();
    const currentLevel = companion.level || 1;
    const newLevel = currentLevel + 1;

    const updates = {
      level: newLevel,
      evasion: (companion.evasion || 10) + 1, // Companions gain +1 Evasion per level
      levelUpChoices: [
        ...(companion.levelUpChoices || []),
        {
          level: newLevel,
          upgrade: upgradeChoice.id,
          timestamp: new Date().toISOString(),
          ...upgradeData
        }
      ]
    };

    // Apply upgrade effects
    switch (upgradeChoice.id) {
      case 'intelligent':
        // Improve existing Experience by +1
        if (upgradeData.experienceToImprove) {
          updates.experiences = companion.experiences.map(exp =>
            exp.id === upgradeData.experienceToImprove
              ? { ...exp, bonus: (exp.bonus || 0) + 1 }
              : exp
          );
        }
        break;

      case 'vicious':
        if (upgradeData.upgradeType === 'damage') {
          // Upgrade damage die
          const dieProgression = ['d6', 'd8', 'd10', 'd12'];
          const currentIndex = dieProgression.indexOf(companion.damageDie);
          if (currentIndex < dieProgression.length - 1) {
            updates.damageDie = dieProgression[currentIndex + 1];
          }
        } else if (upgradeData.upgradeType === 'range') {
          // Upgrade range
          const rangeProgression = ['Melee', 'Close', 'Far'];
          const currentIndex = rangeProgression.indexOf(companion.damageRange);
          if (currentIndex < rangeProgression.length - 1) {
            updates.damageRange = rangeProgression[currentIndex + 1];
          }
        }
        break;

      case 'resilient':
        updates.maxStress = (companion.maxStress || 3) + 1;
        break;

      case 'aware':
        updates.evasion = (companion.evasion || 10) + 2;
        break;

      // Upgrades that affect ranger (Light in the Dark, Creature Comfort, Armored, Bonded)
      // are tracked in levelUpChoices but don't modify companion stats directly
      default:
        // No stat changes needed
        break;
    }

    await updateDoc(companionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    toast.success(`${companion.name} learned ${upgradeChoice.name}!`);

  } catch (error) {
    console.error('Error leveling up companion:', error);
    toast.error('Failed to level up companion');
    throw error;
  }
};

/**
 * Auto-level companion to match ranger's level (for retroactive companions)
 * This is called when creating a companion for a higher-level ranger
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @param {number} targetLevel - Target level to reach
 * @returns {Promise<Array>} Array of pending upgrade choices needed
 */
export const autoLevelCompanion = async (rangerId, companionId, targetLevel) => {
  try {
    const companionRef = doc(db, 'characters', rangerId, 'companion', companionId);
    const companionSnap = await getDoc(companionRef);

    if (!companionSnap.exists()) {
      throw new Error('Companion not found');
    }

    const companion = companionSnap.data();
    const currentLevel = companion.level || 1;
    const levelsToGain = targetLevel - currentLevel;

    if (levelsToGain <= 0) {
      return []; // Already at or above target level
    }

    // Calculate evasion for target level
    // Companions start at 10 evasion and gain +1 per level
    // At level 1: 10, at level 2: 11, at level 3: 12, etc.
    const targetEvasion = 10 + (targetLevel - 1);

    // Update companion level and mark that upgrades are pending
    await updateDoc(companionRef, {
      level: targetLevel,
      evasion: targetEvasion, // Auto-calculate evasion based on level
      pendingUpgrades: levelsToGain,
      updatedAt: serverTimestamp()
    });

    console.log(`🎓 Set pendingUpgrades to ${levelsToGain} for companion at level ${targetLevel}`);

    toast.success(
      `${companion.name} is ready to learn ${levelsToGain} new ability${levelsToGain > 1 ? 'ies' : 'y'}!`,
      { duration: 4000 }
    );

    // Return number of pending choices
    return Array(levelsToGain).fill(null).map((_, i) => ({
      level: currentLevel + i + 1,
      pending: true
    }));

  } catch (error) {
    console.error('Error auto-leveling companion:', error);
    toast.error('Failed to auto-level companion');
    throw error;
  }
};

/**
 * Apply a pending upgrade choice (for retroactive leveling)
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @param {Object} upgradeChoice - The upgrade being applied
 * @param {Object} upgradeData - Additional data for the upgrade
 * @returns {Promise<number>} Remaining pending upgrades
 */
export const applyPendingUpgrade = async (rangerId, companionId, upgradeChoice, upgradeData = {}) => {
  try {
    const companionRef = doc(db, 'characters', rangerId, 'companion', companionId);
    const companionSnap = await getDoc(companionRef);

    if (!companionSnap.exists()) {
      throw new Error('Companion not found');
    }

    const companion = companionSnap.data();
    const pendingUpgrades = companion.pendingUpgrades || 0;

    if (pendingUpgrades <= 0) {
      throw new Error('No pending upgrades available');
    }

    // Apply the upgrade (similar to levelUpCompanion but without level increment)
    const updates = {
      pendingUpgrades: pendingUpgrades - 1,
      levelUpChoices: [
        ...(companion.levelUpChoices || []),
        {
          level: companion.level - pendingUpgrades + 1,
          upgrade: upgradeChoice.id,
          timestamp: new Date().toISOString(),
          retroactive: true,
          ...upgradeData
        }
      ]
    };

    // Apply upgrade effects (same logic as levelUpCompanion)
    switch (upgradeChoice.id) {
      case 'intelligent':
        // Improve existing Experience by +1
        if (upgradeData.experienceToImprove) {
          updates.experiences = companion.experiences.map(exp =>
            exp.id === upgradeData.experienceToImprove
              ? { ...exp, bonus: (exp.bonus || 0) + 1 }
              : exp
          );
        }
        break;

      case 'vicious':
        if (upgradeData.upgradeType === 'damage') {
          const dieProgression = ['d6', 'd8', 'd10', 'd12'];
          const currentIndex = dieProgression.indexOf(companion.damageDie);
          if (currentIndex < dieProgression.length - 1) {
            updates.damageDie = dieProgression[currentIndex + 1];
          }
        } else if (upgradeData.upgradeType === 'range') {
          const rangeProgression = ['Melee', 'Close', 'Far'];
          const currentIndex = rangeProgression.indexOf(companion.damageRange);
          if (currentIndex < rangeProgression.length - 1) {
            updates.damageRange = rangeProgression[currentIndex + 1];
          }
        }
        break;

      case 'resilient':
        updates.maxStress = (companion.maxStress || 3) + 1;
        break;

      case 'aware':
        updates.evasion = (companion.evasion || 10) + 2;
        break;

      default:
        break;
    }

    await updateDoc(companionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    const remaining = pendingUpgrades - 1;
    if (remaining > 0) {
      toast.success(`${companion.name} learned ${upgradeChoice.name}! ${remaining} more upgrade${remaining > 1 ? 's' : ''} to choose.`);
    } else {
      toast.success(`${companion.name} learned ${upgradeChoice.name}! All upgrades complete.`);
    }

    return remaining;

  } catch (error) {
    console.error('Error applying pending upgrade:', error);
    toast.error('Failed to apply upgrade');
    throw error;
  }
};

/**
 * Mark companion stress
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @param {number} amount - Amount of stress to add
 * @returns {Promise<void>}
 */
export const markCompanionStress = async (rangerId, companionId, amount = 1) => {
  try {
    const companionRef = doc(db, 'characters', rangerId, 'companion', companionId);
    const companionSnap = await getDoc(companionRef);

    if (!companionSnap.exists()) {
      throw new Error('Companion not found');
    }

    const companion = companionSnap.data();
    const newStress = Math.min((companion.stress || 0) + amount, companion.maxStress);

    await updateDoc(companionRef, {
      stress: newStress,
      updatedAt: serverTimestamp()
    });

    if (newStress >= companion.maxStress) {
      toast.error(`${companion.name} is dropped out! They'll return after a long rest.`);
    }

  } catch (error) {
    console.error('Error marking companion stress:', error);
    toast.error('Failed to mark stress');
    throw error;
  }
};

/**
 * Clear companion stress (long rest)
 * @param {string} rangerId - Character ID of the ranger
 * @param {string} companionId - Companion document ID
 * @returns {Promise<void>}
 */
export const clearCompanionStress = async (rangerId, companionId) => {
  try {
    await updateCompanion(rangerId, companionId, { stress: 0 });
    toast.success('Companion stress cleared!');
  } catch (error) {
    console.error('Error clearing companion stress:', error);
    toast.error('Failed to clear stress');
    throw error;
  }
};

/**
 * Check if a ranger has specific companion upgrades
 * @param {Object} companion - Companion data
 * @param {string} upgradeId - Upgrade ID to check
 * @returns {boolean}
 */
export const hasCompanionUpgrade = (companion, upgradeId) => {
  if (!companion || !companion.levelUpChoices) return false;
  return companion.levelUpChoices.some(choice => choice.upgrade === upgradeId);
};

/**
 * Get count of a specific upgrade (for stacking upgrades like Intelligent)
 * @param {Object} companion - Companion data
 * @param {string} upgradeId - Upgrade ID to check
 * @returns {number}
 */
export const getCompanionUpgradeCount = (companion, upgradeId) => {
  if (!companion || !companion.levelUpChoices) return 0;
  return companion.levelUpChoices.filter(choice => choice.upgrade === upgradeId).length;
};
